
import React, { useState, useMemo } from 'react';
import { useCountry } from '@/contexts/country';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CountryFlag from './CountryFlag';
import CountryFilters, { CountryFilterType, ViewType } from './CountryFilters';
import CountryRegionalView from './CountryRegionalView';
import { useCountriesWithRegions } from '@/hooks/useCountriesWithRegions';

const CountryList = () => {
  const { setCountry } = useCountry();
  const navigate = useNavigate();
  const { countries, isLoading, error } = useCountriesWithRegions();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<CountryFilterType>('all');
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [viewType, setViewType] = useState<ViewType>('list');

  const handleSelectCountry = (code: string) => {
    setCountry(code);
    navigate(`/insights/${code}`);
  };

  const filteredCountries = useMemo(() => {
    return countries.filter(country => {
      // Search filter
      if (searchQuery && !country.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      // Status filter
      if (filter === 'active' && !country.active) return false;
      if (filter === 'coming-soon' && country.active) return false;
      
      // Region filter
      if (selectedRegion) {
        if (selectedRegion === 'no-region') {
          return !country.region;
        } else {
          return country.region?.id === selectedRegion;
        }
      }
      
      return true;
    });
  }, [countries, searchQuery, filter, selectedRegion]);

  // Extract available regions from countries data
  const availableRegions = useMemo(() => {
    console.log('🔍 CountryList - countries data:', countries);
    const regionsMap = new Map();
    countries.forEach(country => {
      console.log('🔍 Processing country:', country.name, 'region:', country.region);
      if (country.region) {
        regionsMap.set(country.region.id, country.region);
      }
    });
    const regions = Array.from(regionsMap.values());
    console.log('🔍 CountryList - availableRegions:', regions);
    return regions;
  }, [countries]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center">
          <div className="animate-pulse text-muted-foreground">Loading countries...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center">
          <div className="text-red-600">Error loading countries. Please try again.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CountryFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filter={filter}
        onFilterChange={setFilter}
        selectedRegion={selectedRegion}
        onRegionChange={setSelectedRegion}
        viewType={viewType}
        onViewTypeChange={setViewType}
        availableRegions={availableRegions}
      />

      {viewType === 'region' ? (
        <CountryRegionalView
          countries={filteredCountries}
          onSelectCountry={handleSelectCountry}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCountries.map(country => (
            <Card key={country.code} className={country.active ? 'border-nation-teal' : 'border-gray-200'}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <CountryFlag countryCode={country.code} className="h-5 w-6" />
                    <CardTitle className="text-lg font-semibold text-nation-blue">
                      {country.name}
                    </CardTitle>
                  </div>
                  {country.active ? (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                      <Check size={12} className="mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full flex items-center">
                      <Clock size={12} className="mr-1" />
                      Coming Soon
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {country.active ? (
                  <Button 
                    variant="default" 
                    className="w-full bg-nation-teal hover:bg-nation-teal-dark"
                    onClick={() => handleSelectCountry(country.code)}
                  >
                    View {country.name}
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full" disabled>
                    Not Available Yet
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
          
          {filteredCountries.length === 0 && (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground">No countries found matching your criteria.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CountryList;
